import { Suspense, type ComponentType } from 'react';
import { useApproach } from '@/hooks/useApproach';

interface LazySectionProps {
  /** DOM id and anchor target — must exist even before the chunk mounts. */
  readonly id: string;
  /** Reserved height before mount, so approach-mounting costs no CLS. */
  readonly minHeight?: string;
  /** An already-`lazy()`-wrapped component, created once at module scope. */
  readonly Component: ComponentType;
}

/**
 * The real fix for the "every below-the-fold chunk executes and every
 * ScrollTrigger initialises at load" cost that capped v7's mobile TBT.
 *
 * `App.tsx` used to wrap all seven lazy sections in one shared `<Suspense>`
 * that rendered immediately — nothing gated *when* a chunk's own module code
 * ran, only whether its import had resolved. `LazySection` gates the mount
 * itself: the semantic `<section id>` — the thing anchor links, the nav, and
 * `useActiveSection` all target — exists in the DOM from first render, but
 * the actual component (and everything it imports: GSAP, R3F, its own
 * ScrollTriggers) doesn't mount until the reader is `useApproach`'s
 * lookahead away from seeing it.
 *
 * `minHeight` is cleared the moment content mounts rather than kept
 * permanently: it exists purely to stop the section collapsing to zero
 * height pre-mount (which would otherwise read as a layout shift once real
 * content pops in), not to constrain what the mounted section actually is.
 */
export function LazySection({ id, minHeight = '100svh', Component }: LazySectionProps) {
  const [ref, approaching] = useApproach<HTMLElement>(1.5);

  return (
    <section id={id} ref={ref} style={approaching ? undefined : { minHeight }}>
      {approaching && (
        <Suspense fallback={null}>
          <Component />
        </Suspense>
      )}
    </section>
  );
}
